/**
 * DisclosureStats Component
 *
 * Displays 3 stat cards for disclosure tracking:
 * - Total Active Loans
 * - ITOP Missing (danger highlight)
 * - Initial Packages Sent MTD
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, AlertTriangle, Calendar } from 'lucide-react';
import type { DisclosureStats as DisclosureStatsType } from '@/types/disclosure-dashboard';
import { cn } from '@/lib/utils';

interface DisclosureStatsProps {
  stats: DisclosureStatsType;
  loading?: boolean;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  loading?: boolean;
}

function StatCard({ title, value, icon, variant = 'default', loading }: StatCardProps) {
  const variantStyles = {
    default: 'bg-card',
    danger: 'bg-red-50 border-red-200',
    warning: 'bg-amber-50 border-amber-200',
    success: 'bg-green-50 border-green-200'
  };

  const iconStyles = {
    default: 'text-muted-foreground',
    danger: 'text-red-600',
    warning: 'text-amber-600',
    success: 'text-green-600'
  };

  const valueStyles = {
    default: 'text-foreground',
    danger: 'text-red-700',
    warning: 'text-amber-700',
    success: 'text-green-700'
  };

  return (
    <Card className={cn('border', variantStyles[variant])}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-1" />
            ) : (
              <p className={cn('text-2xl font-bold mt-1', valueStyles[variant])}>
                {value.toLocaleString()}
              </p>
            )}
          </div>
          <div className={cn('p-2 rounded-full bg-background/50', iconStyles[variant])}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DisclosureStats({ stats, loading }: DisclosureStatsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <StatCard
        title="Total Active Loans"
        value={stats.totalActiveLoans}
        icon={<FileText className="h-5 w-5" />}
        variant="default"
        loading={loading}
      />
      <StatCard
        title="ITOP Missing"
        value={stats.itopMissing}
        icon={<AlertTriangle className="h-5 w-5" />}
        variant={stats.itopMissing > 0 ? 'danger' : 'default'}
        loading={loading}
      />
      <StatCard
        title="Initial Packages MTD"
        value={stats.initialPackagesMtd}
        icon={<Calendar className="h-5 w-5" />}
        variant="default"
        loading={loading}
      />
    </div>
  );
}
